import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('ebd_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const formatBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatNum = (n) => (Number(n) || 0).toLocaleString('pt-BR');

const DashboardSuperAdmin = ({ setCurrentView }) => {
  const [global, setGlobal] = useState(null);
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('visao');

  // Caches por aba
  const [atividade, setAtividade] = useState(null);
  const [financeiro, setFinanceiro] = useState(null);
  const [crescimento, setCrescimento] = useState(null);
  const [loadingTab, setLoadingTab] = useState(false);

  const loadGlobal = useCallback(async () => {
    setLoadingGlobal(true);
    setError('');
    try {
      const r = await axios.get(`${API}/metricas/global`, { headers: getAuthHeaders() });
      setGlobal(r.data);
    } catch (err) {
      setError('Erro ao carregar métricas: ' + (err.response?.data?.detail || err.message));
    }
    setLoadingGlobal(false);
  }, []);

  useEffect(() => { loadGlobal(); }, [loadGlobal]);

  const loadTab = useCallback(async (which) => {
    setLoadingTab(true);
    try {
      if (which === 'atividade' && !atividade) {
        const r = await axios.get(`${API}/metricas/atividade`, { headers: getAuthHeaders() });
        setAtividade(r.data);
      } else if (which === 'financeiro' && !financeiro) {
        const r = await axios.get(`${API}/metricas/financeiro`, { headers: getAuthHeaders() });
        setFinanceiro(r.data);
      } else if (which === 'crescimento' && !crescimento) {
        const r = await axios.get(`${API}/metricas/crescimento`, { headers: getAuthHeaders() });
        setCrescimento(r.data);
      }
    } catch (err) {
      console.error('Erro ao carregar aba:', err);
    }
    setLoadingTab(false);
  }, [atividade, financeiro, crescimento]);

  useEffect(() => {
    if (tab !== 'visao') loadTab(tab);
  }, [tab, loadTab]);

  const reloadAll = () => {
    setAtividade(null); setFinanceiro(null); setCrescimento(null);
    loadGlobal();
    if (tab !== 'visao') loadTab(tab);
  };

  if (loadingGlobal) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-500">Carregando métricas…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded-lg">{error}</div>
          <button onClick={loadGlobal} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg">Tentar novamente</button>
        </div>
      </div>
    );
  }

  const g = global || {};
  const igrejas = g.igrejas || {};
  const pagamentos = g.pagamentos || {};
  const financeiroG = g.financeiro || {};
  const sessoes = g.sessoes || {};

  const valorEmAtraso = (pagamentos.lista_atraso || []).reduce((acc, i) => acc + (i.valor_mensal || 0), 0);

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Painel SaaS</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Atualizado em {new Date(g.atualizado_em).toLocaleString('pt-BR')}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={reloadAll} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">
              Atualizar
            </button>
            <button onClick={() => setCurrentView('contas-receber')} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium">
              Contas a receber
            </button>
            <button onClick={() => setCurrentView('gerenciar-igrejas')} className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-medium">
              Igrejas
            </button>
          </div>
        </div>

        {/* 4 KPIs principais */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Kpi label="Receita mensal" value={formatBRL(financeiroG.receita_mensal_estimada)} sub={`${igrejas.pagas || 0} assinante(s)`} accent="emerald" />
          <Kpi label="Igrejas ativas" value={formatNum(igrejas.ativas)} sub={`${igrejas.trial || 0} trial · ${igrejas.inativas || 0} inativa(s)`} accent="blue" />
          <Kpi label="Online agora" value={formatNum(sessoes.ativas)} sub="sessões válidas" accent="indigo" />
          <Kpi label="Em atraso" value={formatBRL(valorEmAtraso)} sub={`${pagamentos.em_atraso || 0} igreja(s)`} accent="red" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-gray-200">
          {[
            { v: 'visao', label: 'Visão geral' },
            { v: 'atividade', label: 'Atividade' },
            { v: 'financeiro', label: 'Financeiro' },
            { v: 'crescimento', label: 'Crescimento' },
          ].map(t => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2 text-sm font-semibold transition-colors -mb-px border-b-2 ${
                tab === t.v
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'visao' && <VisaoGeral g={g} />}
        {tab === 'atividade' && (loadingTab && !atividade ? <Skeleton /> : <AbaAtividade data={atividade} />)}
        {tab === 'financeiro' && (loadingTab && !financeiro ? <Skeleton /> : <AbaFinanceiro data={financeiro} />)}
        {tab === 'crescimento' && (loadingTab && !crescimento ? <Skeleton /> : <AbaCrescimento data={crescimento} />)}
      </div>
    </div>
  );
};

const Kpi = ({ label, value, sub, accent = 'blue' }) => {
  const accents = {
    blue: 'border-l-blue-500',
    emerald: 'border-l-emerald-500',
    indigo: 'border-l-indigo-500',
    red: 'border-l-red-500',
    amber: 'border-l-amber-500',
  };
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${accents[accent]} p-4`}>
      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
};

const Skeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-400 text-sm">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
    <p className="mt-3">Carregando…</p>
  </div>
);

const VisaoGeral = ({ g }) => {
  const usuarios = g.usuarios || {};
  const alunos = g.alunos || {};
  const turmas = g.turmas || {};
  const chamadas = g.chamadas || {};
  const igrejas = g.igrejas || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Kpi label="Total usuários" value={formatNum(usuarios.total)} accent="blue" />
      <Kpi label="Total alunos" value={formatNum(alunos.total)} accent="emerald" />
      <Kpi label="Total turmas" value={formatNum(turmas.total)} accent="indigo" />
      <Kpi label="Chamadas (7d)" value={formatNum(chamadas.ultimos_7d)} sub={`${formatNum(chamadas.total)} no total`} accent="amber" />
      <Kpi label="Trials expirados" value={formatNum(igrejas.trials_expirados)} accent="red" />
      <Kpi label="Novas (7d)" value={formatNum(igrejas.novas_7d)} accent="emerald" />
      <Kpi label="Novas (30d)" value={formatNum(igrejas.novas_30d)} accent="blue" />
      <Kpi label="Desativadas" value={formatNum(igrejas.inativas)} accent="amber" />
    </div>
  );
};

const AbaAtividade = ({ data }) => {
  if (!data) return null;
  const maxDia = Math.max(...(data.por_dia || []).map(d => d.count), 1);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Logins hoje" value={formatNum(data.logins_hoje)} accent="emerald" />
        <Kpi label="Logins (7 dias)" value={formatNum(data.logins_7d)} accent="blue" />
        <Kpi label="Logins (30 dias)" value={formatNum(data.logins_30d)} accent="indigo" />
        <Kpi label="Logins (1 ano)" value={formatNum(data.logins_ano)} sub={`${formatNum(data.usuarios_ativos_30d)} usuários únicos/30d`} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Logins por dia (últimos 30 dias)">
          {data.por_dia && data.por_dia.length ? (
            <div className="flex items-end gap-1 h-40 px-2">
              {data.por_dia.map((d) => (
                <div key={d.data} className="flex-1 flex flex-col items-center justify-end" title={`${d.label}: ${d.count}`}>
                  <div className="w-full bg-blue-500 rounded-t" style={{ height: `${(d.count / maxDia) * 100}%`, minHeight: d.count ? 4 : 0 }} />
                </div>
              ))}
            </div>
          ) : (
            <Empty>Sem registros ainda</Empty>
          )}
          <div className="flex justify-between text-[10px] text-gray-500 px-2 mt-1">
            <span>{data.por_dia?.[0]?.label || ''}</span>
            <span>{data.por_dia?.[data.por_dia.length - 1]?.label || ''}</span>
          </div>
        </Section>

        <Section title={`Online agora (${data.online_count || 0})`}>
          {(data.online_agora || []).length === 0 ? (
            <Empty>Ninguém logado no momento</Empty>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {data.online_agora.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between py-1.5 border-b border-gray-100 text-sm">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{u.nome}</p>
                    <p className="text-xs text-gray-500 truncate">{u.igreja_nome || '—'} · {u.tipo}</p>
                  </div>
                  <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                    {u.desde ? new Date(u.desde).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section title={`Igrejas frias (sem login há +30 dias) — ${data.igrejas_frias_total || 0}`}>
        {(data.igrejas_frias || []).length === 0 ? (
          <Empty>Todas as igrejas ativas tiveram login recente</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Igreja</th>
                  <th className="px-3 py-2 text-left font-semibold">Conta</th>
                  <th className="px-3 py-2 text-left font-semibold">Último login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.igrejas_frias.map(ig => (
                  <tr key={ig.igreja_id}>
                    <td className="px-3 py-2 font-medium text-gray-800">{ig.nome}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{ig.tipo_conta}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {ig.ultimo_login ? new Date(ig.ultimo_login).toLocaleDateString('pt-BR') : <span className="text-red-600">nunca</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
};

const AbaFinanceiro = ({ data }) => {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Receita mensal" value={formatBRL(data.receita_mensal_estimada)} accent="emerald" />
        <Kpi label="Em atraso (R$)" value={formatBRL(data.valor_em_atraso)} sub={`${data.em_atraso} igreja(s)`} accent="red" />
        <Kpi label="Vencendo em 7 dias" value={formatNum(data.proximos_7d)} accent="amber" />
        <Kpi label="Em dia" value={formatNum(data.em_dia)} sub={`${data.sem_vencimento} sem vencimento`} accent="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title={`Em atraso (${data.em_atraso})`} accent="red">
          <ListaPagamento items={data.lista_atraso} mode="atraso" />
        </Section>
        <Section title={`Próximos vencimentos (${data.proximos_7d})`} accent="amber">
          <ListaPagamento items={data.lista_proximos} mode="proximos" />
        </Section>
      </div>
    </div>
  );
};

const ListaPagamento = ({ items, mode }) => {
  if (!items || items.length === 0) return <Empty>Nada por aqui</Empty>;
  return (
    <div className="space-y-1.5 max-h-80 overflow-y-auto">
      {items.map(ig => (
        <div key={ig.igreja_id} className="flex items-center justify-between py-1.5 px-2 border-b border-gray-100 text-sm">
          <div className="min-w-0">
            <p className="font-semibold text-gray-800 truncate">{ig.nome}</p>
            <p className="text-xs text-gray-500">
              {formatBRL(ig.valor_mensal || 0)}{ig.dia_vencimento ? ` · venc. dia ${ig.dia_vencimento}` : ''}
            </p>
          </div>
          {mode === 'atraso' ? (
            <span className="px-2 py-0.5 bg-red-50 text-red-700 text-xs font-bold rounded-full ml-2 flex-shrink-0">
              {ig.dias_atraso}d atraso
            </span>
          ) : (
            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-bold rounded-full ml-2 flex-shrink-0">
              {ig.dias_restantes === 0 ? 'Hoje' : ig.dias_restantes === 1 ? 'Amanhã' : `${ig.dias_restantes}d`}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const AbaCrescimento = ({ data }) => {
  if (!data) return null;
  const maxCad = Math.max(...(data.cadastros_por_mes || []).map(m => m.count), 1);
  const maxAlunos = Math.max(...(data.top_igrejas || []).map(i => i.total_alunos), 1);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Novas (7 dias)" value={formatNum(data.novas_7d)} accent="emerald" />
        <Kpi label="Novas (30 dias)" value={formatNum(data.novas_30d)} accent="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Cadastros por mês">
          <div className="space-y-2">
            {(data.cadastros_por_mes || []).map(m => (
              <div key={m.mes} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-16">{m.label}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                  <div
                    className="bg-blue-500 h-full rounded-full flex items-center justify-end pr-2"
                    style={{ width: `${(m.count / maxCad) * 100}%` }}
                  >
                    {m.count > 0 && <span className="text-white text-[10px] font-bold">{m.count}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Top 10 igrejas (por alunos)">
          {(data.top_igrejas || []).length === 0 ? <Empty>Sem dados</Empty> : (
            <div className="space-y-2">
              {data.top_igrejas.map((ig, idx) => (
                <div key={ig.igreja_id} className="flex items-center gap-3 text-sm">
                  <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  <span className="flex-1 truncate text-gray-800 font-medium">{ig.nome}</span>
                  <div className="w-32 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${(ig.total_alunos / maxAlunos) * 100}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 w-10 text-right tabular-nums">{ig.total_alunos}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section title="Distribuição por plano">
        <div className="flex flex-wrap gap-2">
          {(data.distribuicao_planos || []).map(p => (
            <div key={p.plano} className="px-3 py-2 rounded border border-gray-200 bg-gray-50">
              <p className="text-lg font-bold text-gray-800 tabular-nums">{p.count}</p>
              <p className="text-xs text-gray-500 capitalize">{p.plano}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

const Section = ({ title, children, accent }) => {
  const accents = { red: 'text-red-700', amber: 'text-amber-700', blue: 'text-blue-700' };
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className={`text-sm font-bold mb-3 ${accents[accent] || 'text-gray-800'}`}>{title}</h3>
      {children}
    </div>
  );
};

const Empty = ({ children }) => (
  <p className="text-gray-400 text-center py-6 text-sm">{children}</p>
);

export default DashboardSuperAdmin;
